// Holes a cowboy punched through house walls at flight speed (sim/destruct.ts BREACH, James 2026-10-24).
// The wall itself is cut by the town shader (town_burn.ts discards inside each hole's ragged outline); this
// draws what you see through the gap and round its lip:
//  - the room behind: an inside-out box pushed into the wall (one instanced unlit draw), warm and dim, so the
//    hole has depth and never shows the street through the house;
//  - the wall's own torn skin: the thickness of the wall round the exact outline the shader cuts (a cartoon
//    spread-eagle, splintered board by board) and broken clapboards jutting into the gap, bent the way he went. One merged mesh drawn with
//    the town's own atlas material, every board carrying the paint of the wall it was torn from (its tile, tint
//    and the wall's planar uv carried on across the gap), so a cream front shows cream splinters and a teal one
//    teal. Its vertices are tagged BLD_LOOSE so the hole cut and the char leave them alone.
// Rebuilt only when a hole is added or a building's stage changes; nothing allocated per frame.

import * as THREE from 'three';
import type { World } from '../../../sim/world';
import { BREACH, BStage } from '../../../sim/destruct';
import { hash32, Rng } from '../../../sim/core';
import { unlitMaterial } from '../materials';
import { TILES } from './town_atlas';

/** `bld` tag for loose pieces in the town's solid material: not cut by holes, not charred, not folded */
export const BLD_LOOSE = -200;
const DEPTH = 1.6;
/** the wall's thickness round the lip */
const THICK = 0.24;
/** the hole is an upright body-sized oval, R wide and R * HOLE_VY tall either side of its centre */
export const HOLE_VY = 0.92;
const MAX_VERTS = BREACH.maxHoles * 3200;
/** boards per atlas tile for the lap sidings (public/assets/town/_src/gen_atlas.py clapboard planks=) */
const COURSES: [readonly number[], number][] = [[TILES.clap, 7], [TILES.clap2, 9]];

/** A hole's outline seed (0..63), shared by the shader's cut (town_burn.ts) and the lining here. */
export function holeSeed(h: { x: number; y: number; z: number }) {
  return hash32(Math.round(h.x * 10), Math.round(h.y * 10), Math.round(h.z * 10), 0x5eed) & 63;
}

// the shader's hash (town_burn.ts ruinHash), so the lining follows the cut exactly
const fract = (x: number) => x - Math.floor(x);
function rHash(x: number, y: number, z: number) {
  x = fract(x * 0.3183099 + 0.1) * 17; y = fract(y * 0.3183099 + 0.1) * 17; z = fract(z * 0.3183099 + 0.1) * 17;
  return fract(x * y * z * (x + y + z));
}
/**
 * How far the hole reaches along course k on one side of its middle (FRAG_HOLE): each course of siding snaps at its
 * own length, inside the flier's outline; now and then a board tears on along its run before it lets go.
 * `ch` = course height, `ph` = where a lap line sits.
 */
function holeHalf(k: number, side: number, sd: number, ch: number, ph: number, t: number, fit: number, lim: number) {
  const e = ((k + 0.5) * ch + ph) / BREACH.r / HOLE_VY;
  if (Math.abs(e) >= 1) return 0;
  // its end torn on a slant: t = -0.5 at the bottom of the course, 0.5 at the top
  let w = (0.88 + 0.2 * rHash(k * 1.7 + 0.3, side * 3.1 + 5, sd)) * outline(BODY, e)
    + (rHash(k * 2.9 + 1.1, side * 1.3 + 2, sd) - 0.5) * t * 0.35;
  if (Math.abs(e) < 0.75 && rHash(k * 3.7 + 0.9, side * 2.3 + 1, sd) < 0.12) w += 0.22 * (0.6 + 0.4 * rHash(k * 1.1 + 4, side * 0.7 + 3, sd));
  // and splintered into a couple of teeth along it, not sawn
  w += (0.2 + 0.3 * rHash(k * 2.2 + 3.1, side * 3.3 + 1, sd))
    * (Math.abs(fract(t * (0.6 + 0.5 * rHash(k * 0.9 + 5.7, side * 2.6 + 4, sd)) + rHash(k * 4.3 + 0.2, side * 1.9 + 6, sd)) - 0.5) - 0.25);
  // never past the edge of the panel it's in (`lim`, from the middle of this course)
  return Math.min(BREACH.r * fit * w, lim);
}
/** the wall left standing between his legs, either side of the middle */
function holeGap(k: number, ch: number, ph: number, fit: number) {
  const g = BREACH.r * fit * outline(GAP, ((k + 0.5) * ch + ph) / BREACH.r / HOLE_VY);
  return g < 0.1 ? 0 : g; // a sliver between his knees would only be a scrap left hanging
}
/** He went through leaning (FRAG_HOLE): the middle of course k slides sideways with its height. */
export function holeLean(sd: number) {
  return (rHash(sd * 0.7 + 1.3, 7, 3) < 0.5 ? -1 : 1) * (0.06 + 0.06 * rHash(sd * 1.3 + 2.1, 5, 9));
}
function holeMid(k: number, sd: number, ch: number, ph: number, fit: number) {
  return holeLean(sd) * fit * ((k + 0.5) * ch + ph);
}
/**
 * The flier's outline, the cartoon spread-eagle: bottom (e = -1) to top (1), half-widths in hole radii between these
 * heights. Legs apart, a waist, both arms flung out, the neck, the head. FRAG_HOLE runs the same curves.
 */
export const BODY = [[-1, 0.62], [-0.5, 0.5], [-0.3, 0.44], [0, 0.4], [0.1, 0.46], [0.16, 1.2], [0.42, 1.24], [0.48, 0.5],
  [0.55, 0.24], [0.62, 0.38], [0.9, 0.4], [1, 0.2]] as const;
/** and the gap between the legs */
export const GAP = [[-1, 0.3], [-0.62, 0.2], [-0.56, 0], [1, 0]] as const;
function outline(pts: readonly (readonly [number, number])[], e: number) {
  if (e < pts[0][0]) return 0;
  for (let i = 1; i < pts.length; i++) if (e <= pts[i][0]) { const [x0, y0] = pts[i - 1], [x1, y1] = pts[i]; return y0 + (y1 - y0) * (e - x0) / (x1 - x0); }
  return 0;
}
/** the parts of spans `a` not covered by spans `b` (both sorted, disjoint) */
function spanMinus(a: number[][], b: number[][]) {
  const out: number[][] = [];
  for (const [a0, a1] of a) {
    let x = a0;
    for (const [b0, b1] of b) {
      if (b1 <= x || b0 >= a1) continue;
      if (b0 > x) out.push([x, b0]);
      x = Math.max(x, b1);
    }
    if (x < a1) out.push([x, a1]);
  }
  return out.filter(([x0, x1]) => x1 - x0 > 0.01);
}

/** The room behind the hole: unlit, warm and dim, a little light on the floorboards near the lip. */
function cavityGeometry() {
  const g = new THREE.BoxGeometry(1, 1, 1, 4, 12, 6);
  // local +z is out of the wall: the box runs from the wall plane (z = +0.5) back into the room (z = -0.5)
  const pos = g.getAttribute('position'), nrm = g.getAttribute('normal');
  const col = new Float32Array(pos.count * 3), c = new THREE.Color();
  const back = new THREE.Color(0x1c130d), lit = new THREE.Color(0x76583a), side = new THREE.Color(0x2e2016);
  const floor = new THREE.Color(0x7a5a3c), ceil = new THREE.Color(0x1e150f);
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i), ny = nrm.getY(i), nz = nrm.getZ(i);
    const k = z + 0.5; // 1 at the wall, 0 at the back
    if (nz < -0.5) c.copy(back).lerp(lit, Math.max(0, 1 - Math.hypot(x * 2.2, (y + 0.12) * 2.4)) ** 1.5); // the far wall, the light through the hole on it
    else if (ny > 0.5) c.copy(floor).lerp(back, 1 - k * 0.85); // the floor: lit planks fading into the dark
    else if (ny < -0.5) c.copy(ceil);
    else c.copy(side).lerp(back, 1 - k * 0.75);
    col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  // a window in the far wall, daylight through it, and its cross of glazing bars: you're looking into a room
  const box = g.toNonIndexed();
  {
    // the room's walls are boarded: each face banded plank by plank (whole triangles, so the bands stay crisp)
    const bp = box.getAttribute('position'), bc = box.getAttribute('color'), bn = box.getAttribute('normal');
    for (let t = 0; t < bp.count; t += 3) {
      const up = Math.abs(bn.getY(t)) > 0.5;
      const cy = (bp.getY(t) + bp.getY(t + 1) + bp.getY(t + 2)) / 3, cz = (bp.getZ(t) + bp.getZ(t + 1) + bp.getZ(t + 2)) / 3;
      const band = Math.floor((up ? cz : cy) * 12 + 50);
      const k = 0.86 + 0.24 * (((band * 2654435761) >>> 0) % 1000) / 1000;
      for (let v = t; v < t + 3; v++) bc.setXYZ(v, bc.getX(v) * k, bc.getY(v) * k, bc.getZ(v) * k);
    }
  }
  const parts: [THREE.BufferGeometry, number][] = [];
  const pane = (w: number, h: number, x: number, y: number, z: number, c: number) => {
    const p = new THREE.PlaneGeometry(w, h);
    p.rotateY(Math.PI); // its back faces into the room's open side (the material draws back faces)
    p.translate(x, y, z);
    parts.push([p.toNonIndexed(), c]);
  };
  pane(0.26, 0.3, 0.02, 0.14, -0.49, 0xd9b77e); // the glass, lit from outside
  pane(0.31, 0.35, 0.02, 0.14, -0.495, 0x4a3524); // its frame
  pane(0.025, 0.3, 0.02, 0.14, -0.485, 0x3a281a); // the bars
  pane(0.26, 0.02, 0.02, 0.14, -0.485, 0x3a281a);
  pane(0.38, 0.05, 0.02, 0.14 - 0.15 - 0.025, -0.485, 0x2e2016); // the sill
  // a dusty shaft of the window's light falling on the floor
  const shaft = new THREE.PlaneGeometry(0.36, 0.5);
  shaft.rotateX(Math.PI / 2); // facing down: its back looks up into the room
  shaft.translate(0.04, -0.495, -0.2);
  parts.push([shaft.toNonIndexed(), 0x8a6a46]);
  let n = box.getAttribute('position').count;
  for (const [p] of parts) n += p.getAttribute('position').count;
  const P = new Float32Array(n * 3), C = new Float32Array(n * 3), Nn = new Float32Array(n * 3);
  P.set(box.getAttribute('position').array as Float32Array); C.set(box.getAttribute('color').array as Float32Array); Nn.set(box.getAttribute('normal').array as Float32Array);
  let o = box.getAttribute('position').count;
  for (const [p, c] of parts) {
    const k = p.getAttribute('position').count, cc = new THREE.Color(c);
    P.set(p.getAttribute('position').array as Float32Array, o * 3); Nn.set(p.getAttribute('normal').array as Float32Array, o * 3);
    for (let i = 0; i < k; i++) { C[(o + i) * 3] = cc.r; C[(o + i) * 3 + 1] = cc.g; C[(o + i) * 3 + 2] = cc.b; }
    o += k;
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(P, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(Nn, 3));
  out.setAttribute('color', new THREE.BufferAttribute(C, 3));
  return out;
}

/** a template piece, non-indexed, in a unit box: position, normal, and whether each vertex is on a broad face */
interface Tmpl { p: Float32Array; n: Float32Array; face: Uint8Array; count: number }
function tmpl(g: THREE.BufferGeometry): Tmpl {
  const ng = g.index ? g.toNonIndexed() : g;
  ng.computeVertexNormals();
  const p = ng.getAttribute('position').array as Float32Array, n = ng.getAttribute('normal').array as Float32Array;
  const face = new Uint8Array(p.length / 3);
  for (let i = 0; i < face.length; i++) face[i] = Math.abs(n[i * 3 + 2]) > 0.5 ? 1 : 0;
  return { p: p.slice(), n: n.slice(), face, count: face.length };
}
/** one snapped clapboard: square root (hidden in the wall), free end (+x) torn into splinter points */
function boardTmpl() {
  const sh = new THREE.Shape();
  sh.moveTo(-0.5, -0.5); sh.lineTo(0.3, -0.5); sh.lineTo(0.5, -0.28); sh.lineTo(0.22, -0.05); sh.lineTo(0.42, 0.18); sh.lineTo(0.16, 0.34); sh.lineTo(0.26, 0.5); sh.lineTo(-0.5, 0.5); sh.lineTo(-0.5, -0.5);
  const g = new THREE.ExtrudeGeometry(sh, { depth: 1, bevelEnabled: false });
  g.translate(0, 0, -0.5);
  return tmpl(g);
}

/** what the wall is painted with round a hole: its atlas tile, tint, and its planar uv as a function of (u, v) on the wall */
interface Paint { tile: number[]; tint: number[]; tri: number[]; uvs: number[]; off: number; fit: number }
const SIDING = ['clap', 'clap2', 'batten', 'planks', 'brick', 'corrugated', 'shingle', 'stone', 'plain'] as const;
const RAW = TILES.rawwood;

export class BreachView {
  readonly group = new THREE.Group();
  private cavity: THREE.InstancedMesh;
  private skin: THREE.Mesh;
  private geo = new THREE.BufferGeometry();
  private a = {
    p: new Float32Array(MAX_VERTS * 3), n: new Float32Array(MAX_VERTS * 3), uv: new Float32Array(MAX_VERTS * 2),
    tile: new Float32Array(MAX_VERTS * 4), tint: new Float32Array(MAX_VERTS * 4), bld: new Float32Array(MAX_VERTS).fill(BLD_LOOSE),
  };
  private nv = 0;
  private board = boardTmpl();
  private ver = -1;
  private stages: number[] = [];
  /** per building: its vertical wall triangles in the town's solid mesh (found on its first hole) */
  private walls = new Map<number, number[]>();

  constructor(private world: World, private solid: THREE.BufferGeometry, mat: THREE.Material, private rows: THREE.Vector4[], private fits: THREE.Vector2[]) {
    const cm = unlitMaterial({ vertexColors: true, side: THREE.BackSide });
    this.cavity = new THREE.InstancedMesh(cavityGeometry(), cm, BREACH.maxHoles);
    this.cavity.name = 'breachCavity';
    this.cavity.frustumCulled = false; this.cavity.count = 1;
    // compiled with the island (never mid-fight): one instance parked far under the world
    this.cavity.setMatrixAt(0, new THREE.Matrix4().makeTranslation(0, -9000, 0));
    const a = this.a, g = this.geo;
    g.setAttribute('position', new THREE.BufferAttribute(a.p, 3));
    g.setAttribute('normal', new THREE.BufferAttribute(a.n, 3));
    g.setAttribute('uv', new THREE.BufferAttribute(a.uv, 2));
    g.setAttribute('tile', new THREE.BufferAttribute(a.tile, 4));
    g.setAttribute('tint', new THREE.BufferAttribute(a.tint, 4));
    g.setAttribute('bld', new THREE.BufferAttribute(a.bld, 1));
    g.setDrawRange(0, 0);
    // the town's own solid material (already compiled for the street): no new program
    this.skin = new THREE.Mesh(g, mat);
    this.skin.name = 'breachSkin';
    this.skin.frustumCulled = false; this.skin.receiveShadow = true;
    this.group.add(this.cavity, this.skin);
    this.group.name = 'townBreach';
  }

  update() {
    const d = this.world.dest;
    let stale = d.holeVer !== this.ver;
    for (let i = 0; i < d.buildings.length; i++) {
      const st = d.buildings[i].stage;
      if (this.stages[i] !== st) { this.stages[i] = st; stale = true; }
    }
    if (!stale) return;
    this.ver = d.holeVer;
    this.nv = 0;
    let nc = 0;
    const R = BREACH.r, m = new THREE.Matrix4(), q = new THREE.Quaternion(), p = new THREE.Vector3(), s = new THREE.Vector3();
    for (let hi = 0; hi < d.holes.length; hi++) {
      const h = d.holes[hi];
      if (d.buildings[h.b]?.stage === BStage.Collapsed) continue; // the heap took the holes with it
      if (this.nv + 3200 > MAX_VERTS) break;
      const ry = h.face === 0 ? Math.PI / 2 : h.face === 1 ? -Math.PI / 2 : h.face === 2 ? 0 : Math.PI;
      const nx = Math.sin(ry), nz = Math.cos(ry);
      q.setFromAxisAngle(p.set(0, 1, 0), ry);
      const tx = Math.cos(ry), tz = -Math.sin(ry);
      const paint = this.paintAt(h.b, h.x, h.y, h.z, nx, nz, tx, tz);
      // the room: its open face a hair behind the outside of the wall he hit (a shopfront can sit back from the
      // building's line), so that wall's own cut edge frames it
      const w = paint.off - DEPTH / 2 + 0.02;
      p.set(h.x + nx * w, h.y, h.z + nz * w);
      s.set(R * 3.4, R * HOLE_VY * 2.1, DEPTH);
      this.cavity.setMatrixAt(nc++, m.compose(p, q, s));
      // the cut steps along the wall's own siding courses: their height and where a lap line falls, from its uv
      const v0 = this.uvAt(paint, 0, 0)[1], dv = this.uvAt(paint, 0, 1)[1] - v0;
      const n = COURSES.find(([t]) => Math.abs(paint.tile[0] - t[0]) + Math.abs(paint.tile[1] - t[1]) < 1e-4)?.[1] ?? 0;
      let ch = 0.2, ph = 0;
      if (n && Math.abs(dv) > 1e-3) { ch = 1 / (n * Math.abs(dv)); ph = fract(-v0 * n) / (n * dv); ph -= Math.floor(ph / ch) * ch; }
      // narrowed to fit the panel it's in (between a post and a door, say), never below a slim body
      // a body is wider than a shopfront panel between a post and a door: there it takes the door's frame and the
      // trim with it (the cut reaches deeper either side of the wall), and narrows only a little
      const narrow = paint.fit < R * 1.04 + 0.06;
      // in a narrow panel (between a post and a door, say) the post and frame stop the break at the panel's edge
      const fit = narrow ? 0.92 : 1;
      if (hi < this.rows.length) { this.rows[hi].set(ch, ph, n ? paint.off : 0, narrow ? 0.45 : n ? 0.22 : 0.55); this.fits[hi].set(fit, paint.fit - 0.04); }
      this.tear(h, ry, nx, nz, q, paint, ch, ph, fit, paint.fit - 0.04);
    }
    this.cavity.count = Math.max(1, nc);
    if (!nc) this.cavity.setMatrixAt(0, m.makeTranslation(0, -9000, 0));
    this.cavity.instanceMatrix.needsUpdate = true;
    for (const k of ['position', 'normal', 'uv', 'tile', 'tint'] as const) (this.geo.getAttribute(k) as THREE.BufferAttribute).needsUpdate = true;
    this.geo.setDrawRange(0, this.nv);
  }

  /** the paint colour (linear rgb) of the house wall at (x, y, z) facing (nx, nz), for the burst's splinters */
  paintNear(x: number, y: number, z: number, nx: number, nz: number) {
    const b = this.world.dest.buildingAt(x - nx * 0.4, y, z - nz * 0.4);
    if (b < 0) return null;
    const p = this.paintAt(b, x, y, z, nx, nz, nz, -nx);
    return [p.tint[0], p.tint[1], p.tint[2]] as const;
  }

  /** the torn skin round one hole: lining and boards */
  private tear(h: { x: number; y: number; z: number; b: number; entry: boolean }, ry: number, nx: number, nz: number, qw: THREE.Quaternion, paint: Paint, ch: number, ph: number, fit: number, lim: number) {
    const R = BREACH.r, sd = holeSeed(h), tx = Math.cos(ry), tz = -Math.sin(ry);
    // torn boards sit a little in the wall's shadow: the same paint, a shade down
    const shade = [paint.tint[0] * 0.66, paint.tint[1] * 0.66, paint.tint[2] * 0.66, paint.tint[3]];
    const rng = new Rng(hash32(Math.round(h.x * 10), Math.round(h.y * 10), Math.round(h.z * 10), 0xb3ac));
    const inward = h.entry ? -1 : 1;
    // everything sits on the wall the paint came from (the siding can stand a little proud of or behind the facade line)
    const world = (u: number, v: number, w: number, o: THREE.Vector3) => (w += paint.off, o.set(h.x + tx * u + nx * w, h.y + v, h.z + tz * u + nz * w));
    const raw = [0.5, 0.37, 0.26, 0.86], deep = [0.42, 0.31, 0.22, 0.8], dark = [0.42, 0.32, 0.23, 0.76];

    // the courses the hole crosses, and how far it reaches along each, left and right
    const k0 = Math.floor((-R * HOLE_VY - ph) / ch), k1 = Math.floor((R * HOLE_VY - ph) / ch);
    const mid = (k: number) => holeMid(k, sd, ch, ph, fit);
    const half = (k: number, side: number, t = 0) => (k < k0 || k > k1 ? 0 : Math.max(0, holeHalf(k, side, sd, ch, ph, t, fit, lim - side * mid(k))));
    // lap siding over studs is a thick wall; a shopfront panel or trim board is thin
    const lapWall = COURSES.some(([t]) => Math.abs(paint.tile[0] - t[0]) + Math.abs(paint.tile[1] - t[1]) < 1e-4);
    const thick = lapWall ? THICK : 0.12;
    const N = new THREE.Vector3(), dep = (a: number, b: number) => -thick * (0.6 + 0.8 * rHash(a * 2.3, b * 1.9, sd + 0.5));
    // one face of the wall's thickness: the strip (u0,v0)-(u1,v1) on the wall plane, run back into it, facing (fu, fv)
    const lip = (u0: number, v0: number, u1: number, v1: number, fu: number, fv: number, tint: number[]) => {
      // the broken face of the board, then darker and rougher further in where the light doesn't reach
      const d0 = dep(u0, v0), d1 = dep(u1, v1);
      const ws = [world(u0, v0, -0.004, new THREE.Vector3()), world(u1, v1, -0.004, new THREE.Vector3()),
        world(u1, v1, d1 * 0.45, new THREE.Vector3()), world(u0, v0, d0 * 0.45, new THREE.Vector3())];
      // lit as if turned half out of the wall: a break facing away from the sun would otherwise go black
      N.set(tx * fu + nx * 0.9, fv, tz * fu + nz * 0.9).normalize();
      const L = Math.hypot(u1 - u0, v1 - v0) * 2.5;
      this.quad(ws, [[0, 0], [L, 0], [L, 0.3], [0, 0.3]], N, RAW, tint);
      const wb = [ws[3].clone(), ws[2].clone(), world(u1, v1, d1, new THREE.Vector3()), world(u0, v0, d0, new THREE.Vector3())];
      this.quad(wb, [[0, 0.3], [L, 0.3], [L, 0.6], [0, 0.6]], N, RAW, tint === dark ? dark : deep);
    };
    // the open spans of course k at its foot (t = -0.5) or top (0.5), left to right: one, or two where his legs part
    const spans = (k: number, t: number) => {
      const c = mid(k), g = k < k0 || k > k1 ? 0 : holeGap(k, ch, ph, fit), wl = half(k, -1, t), wr = half(k, 1, t);
      if (g < 0.02) return wl + wr > 0.02 ? [[c - wl, c + wr]] : [];
      const out: number[][] = [];
      if (wl > g + 0.02) out.push([c - wl, c - g]);
      if (wr > g + 0.02) out.push([c + g, c + wr]);
      return out;
    };
    for (let k = k0 - 1; k <= k1; k++) {
      const yb = k * ch + ph, yt = yb + ch;
      const lo = spans(k, -0.5), hi = spans(k, 0.5), up = spans(k + 1, -0.5);
      // the snapped board ends, both sides of every span, followed up the course tooth by tooth
      for (let j = 0; j < 8; j++) {
        const ta = -0.5 + j / 8, a = spans(k, ta), b = spans(k, ta + 1 / 8);
        if (a.length !== b.length) continue;
        const va = yb + (ta + 0.5) * ch, vb = va + ch / 8;
        a.forEach(([x0, x1], i) => {
          const tint = [raw, deep, dark][Math.floor(rHash(k * 1.3 + i, x0 * 2.1, sd) * 2.99)];
          lip(x0, va, b[i][0], vb, 1, 0, tint);
          lip(x1, va, b[i][1], vb, -1, 0, tint);
        });
      }
      // the step to the course above: the underside of that course where this one is open and it isn't, the top
      // of this one where that one is open and this isn't
      for (const [x0, x1] of spanMinus(hi, up)) lip(x0, yt, x1, yt, 0, -1, deep);
      for (const [x0, x1] of spanMinus(up, hi)) lip(x0, yt, x1, yt, 0, 1, deep);
    }

    // broken clapboards: some courses leave a splintered stub jutting into the gap from where they snapped, bent the
    // way he went, carrying on the wall's own paint. They sit just behind the wall's face, so the wall hides their roots.
    const b = this.board, e = new THREE.Euler(), qb = new THREE.Quaternion(), mb = new THREE.Matrix4(), mn = new THREE.Matrix3();
    const sc = new THREE.Vector3(), pos = new THREE.Vector3();
    for (let k = k0; k <= k1; k++) {
      const v = (k + 0.5) * ch + ph;
      for (const side of [-1, 1]) {
        const hw = half(k, side), u = mid(k) + side * hw;
        if (hw < 0.08 || v < -R * HOLE_VY * 0.3 || v > R * HOLE_VY * 0.5 || hw < holeGap(k, ch, ph, fit) + 0.1 || hw >= lim - side * mid(k) - 0.01 || rng.next() < (lapWall ? 0.4 : 0.55)) continue; // most courses broke clean at the step
        // a thin shopfront panel shatters short; lap siding can leave a long board hanging
        const stick = lapWall && rng.next() < 0.15 ? rng.range(0.3, 0.55) : lapWall ? rng.range(0.06, 0.18) : rng.range(0.04, 0.1), L = stick + 0.1;
        const cu = u - side * stick + side * L / 2;
        const bend = inward * rng.range(0.1, 0.7) * Math.min(1, stick / 0.4);
        // the torn end (+x) points into the hole: on the right-hand side the board is turned end for end
        e.set(0, side * bend, rng.range(-0.08, 0.08) + (side > 0 ? Math.PI : 0), 'YXZ');
        qb.setFromEuler(e).premultiply(qw);
        const lz = -0.025 + inward * Math.abs(Math.sin(bend)) * L * 0.45;
        world(cu, v, lz, pos);
        sc.set(L, ch * 0.94, 0.07);
        mb.compose(pos, qb, sc); mn.getNormalMatrix(mb);
        this.piece(b, mb, mn, (wp, isFace, lx, ly) => {
          if (!isFace) return [RAW, deep, lx * L * 1.4, ly * 0.4];
          // the face keeps the wall's paint, the uv carried on from where the board sat
          const du = (wp.x - h.x) * tx + (wp.z - h.z) * tz, dv = wp.y - h.y;
          const uv = this.uvAt(paint, du, dv);
          return [paint.tile, shade, uv[0], uv[1]];
        });
      }
    }
  }

  /** append a template piece through matrix `mb`; `skin` gives tile, tint and uv per vertex */
  private piece(t: Tmpl, mb: THREE.Matrix4, mn: THREE.Matrix3,
    skin: (wp: THREE.Vector3, face: boolean, lx: number, ly: number) => [ArrayLike<number>, ArrayLike<number>, number, number]) {
    const a = this.a, wp = new THREE.Vector3(), wn = new THREE.Vector3();
    for (let i = 0; i < t.count; i++) {
      const lx = t.p[i * 3], ly = t.p[i * 3 + 1];
      wp.set(lx, ly, t.p[i * 3 + 2]).applyMatrix4(mb);
      wn.set(t.n[i * 3], t.n[i * 3 + 1], t.n[i * 3 + 2]).applyMatrix3(mn).normalize();
      const [tile, tint, u, v] = skin(wp, t.face[i] === 1, lx, ly);
      this.vert(wp, wn, u, v, tile, tint);
    }
  }

  /** append a quad (4 corners, wound to face `n`) */
  private quad(w: THREE.Vector3[], uv: number[][], n: THREE.Vector3, tile: ArrayLike<number>, tint: ArrayLike<number>) {
    const e1 = w[1].clone().sub(w[0]), e2 = w[2].clone().sub(w[0]);
    const flip = e1.cross(e2).dot(n) < 0;
    const order = flip ? [0, 2, 1, 0, 3, 2] : [0, 1, 2, 0, 2, 3];
    for (const i of order) this.vert(w[i], n, uv[i][0], uv[i][1], tile, tint);
  }

  private vert(p: THREE.Vector3, n: THREE.Vector3, u: number, v: number, tile: ArrayLike<number>, tint: ArrayLike<number>) {
    const a = this.a, i = this.nv++;
    a.p[i * 3] = p.x; a.p[i * 3 + 1] = p.y; a.p[i * 3 + 2] = p.z;
    a.n[i * 3] = n.x; a.n[i * 3 + 1] = n.y; a.n[i * 3 + 2] = n.z;
    a.uv[i * 2] = u; a.uv[i * 2 + 1] = v;
    for (let k = 0; k < 4; k++) { a.tile[i * 4 + k] = tile[k]; a.tint[i * 4 + k] = tint[k]; }
  }

  /** the paint of the wall a hole is in: the frontmost siding triangle of that building's face containing the hole */
  private paintAt(b: number, x: number, y: number, z: number, nx: number, nz: number, tx: number, tz: number): Paint {
    const g = this.solid, P = g.getAttribute('position'), Nn = g.getAttribute('normal'), UV = g.getAttribute('uv');
    const T = g.getAttribute('tile'), TI = g.getAttribute('tint');
    const tris = this.wallsOf(b);
    let best = -1, bestScore = -Infinity, bestOff = 0;
    const uu = [0, 0, 0], vv = [0, 0, 0];
    for (let t = 0; t < tris.length; t += 3) {
      const i0 = tris[t];
      if (Nn.getX(i0) * nx + Nn.getZ(i0) * nz < 0.9) continue;
      const off = (P.getX(i0) - x) * nx + (P.getZ(i0) - z) * nz;
      if (Math.abs(off) > 0.55) continue;
      for (let k = 0; k < 3; k++) { const j = tris[t + k]; uu[k] = (P.getX(j) - x) * tx + (P.getZ(j) - z) * tz; vv[k] = P.getY(j) - y; }
      const inside = inTri(0, 0, uu, vv);
      const sid = SIDING.some((nm) => Math.abs(T.getX(i0) - TILES[nm][0]) + Math.abs(T.getY(i0) - TILES[nm][1]) < 1e-4);
      const cx = (uu[0] + uu[1] + uu[2]) / 3, cy = (vv[0] + vv[1] + vv[2]) / 3;
      const score = (inside ? 100 : 0) + (sid ? 50 : 0) + off * 10 - Math.hypot(cx, cy);
      if (score > bestScore) { bestScore = score; best = t; bestOff = off; }
    }
    if (best < 0) return { tile: [...TILES.clap.slice(0, 4)], tint: [0.86, 0.8, 0.68, 1], tri: [0, 0, 1, 0, 0, 1], uvs: [0, 0, 1, 0, 0, 1], off: 0, fit: 9 };
    const tri: number[] = [], uvs: number[] = [];
    for (let k = 0; k < 3; k++) {
      const j = tris[best + k];
      tri.push((P.getX(j) - x) * tx + (P.getZ(j) - z) * tz, P.getY(j) - y);
      uvs.push(UV.getX(j), UV.getY(j));
    }
    const i0 = tris[best];
    // how much of this panel there is either side of the hole (a shopfront panel between a post and a door is narrow):
    // the run of same-paint triangles in its plane across the hole's middle
    const ivs: number[] = [];
    let lo = Infinity, hi = -Infinity;
    for (let t = 0; t < tris.length; t += 3) {
      const j0 = tris[t];
      if (Nn.getX(j0) * nx + Nn.getZ(j0) * nz < 0.9 || Math.abs(T.getX(j0) - T.getX(i0)) + Math.abs(T.getY(j0) - T.getY(i0)) > 1e-4) continue;
      if (Math.abs((P.getX(j0) - x) * nx + (P.getZ(j0) - z) * nz - bestOff) > 0.03) continue;
      let u0 = Infinity, u1 = -Infinity, v0 = Infinity, v1 = -Infinity;
      for (let k = 0; k < 3; k++) {
        const j = tris[t + k], u = (P.getX(j) - x) * tx + (P.getZ(j) - z) * tz, v = P.getY(j) - y;
        u0 = Math.min(u0, u); u1 = Math.max(u1, u); v0 = Math.min(v0, v); v1 = Math.max(v1, v);
      }
      if (v0 > -0.3 || v1 < 0.3) continue;
      ivs.push(u0, u1);
      if (t === best) { lo = u0; hi = u1; }
    }
    for (let grown = true; grown;) {
      grown = false;
      for (let i = 0; i < ivs.length; i += 2) {
        if (ivs[i] <= hi + 0.05 && ivs[i + 1] >= lo - 0.05 && (ivs[i] < lo - 1e-3 || ivs[i + 1] > hi + 1e-3)) { lo = Math.min(lo, ivs[i]); hi = Math.max(hi, ivs[i + 1]); grown = true; }
      }
    }
    const fit = lo < hi ? Math.min(-lo, hi) : 9;
    return { tile: [T.getX(i0), T.getY(i0), T.getZ(i0), T.getW(i0)], tint: [TI.getX(i0), TI.getY(i0), TI.getZ(i0), Math.max(0.9, TI.getW(i0))], tri, uvs, off: bestOff, fit };
  }

  /** the wall's planar uv at (u, v) on it: the paint triangle's affine map, carried past its edges */
  private uvAt(pt: Paint, u: number, v: number) {
    const [x0, y0, x1, y1, x2, y2] = pt.tri;
    const den = (y1 - y2) * (x0 - x2) + (x2 - x1) * (y0 - y2) || 1;
    const l0 = ((y1 - y2) * (u - x2) + (x2 - x1) * (v - y2)) / den;
    const l1 = ((y2 - y0) * (u - x2) + (x0 - x2) * (v - y2)) / den;
    const l2 = 1 - l0 - l1;
    return [l0 * pt.uvs[0] + l1 * pt.uvs[2] + l2 * pt.uvs[4], l0 * pt.uvs[1] + l1 * pt.uvs[3] + l2 * pt.uvs[5]];
  }

  /** vertex indices (in threes) of building b's upright wall triangles in the solid mesh */
  private wallsOf(b: number) {
    let w = this.walls.get(b);
    if (w) return w;
    w = [];
    const g = this.solid, bld = g.getAttribute('bld'), Nn = g.getAttribute('normal'), idx = g.index;
    const n = idx ? idx.count : bld.count;
    for (let t = 0; t + 2 < n; t += 3) {
      const i0 = idx ? idx.getX(t) : t, i1 = idx ? idx.getX(t + 1) : t + 1, i2 = idx ? idx.getX(t + 2) : t + 2;
      if (i0 === i1) continue; // zeroed (a collapsed body)
      if (Math.round(bld.getX(i0)) !== b + 1 || Math.abs(Nn.getY(i0)) > 0.3) continue;
      w.push(i0, i1, i2);
    }
    this.walls.set(b, w);
    return w;
  }

  dispose() {
    this.cavity.geometry.dispose(); (this.cavity.material as THREE.Material).dispose(); this.cavity.dispose();
    this.geo.dispose(); // the material is the town's
    this.group.removeFromParent();
  }
}

function inTri(px: number, py: number, xs: number[], ys: number[]) {
  const d = (ax: number, ay: number, bx: number, by: number) => (px - bx) * (ay - by) - (ax - bx) * (py - by);
  const d1 = d(xs[0], ys[0], xs[1], ys[1]), d2 = d(xs[1], ys[1], xs[2], ys[2]), d3 = d(xs[2], ys[2], xs[0], ys[0]);
  return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
}
